import { useState } from 'react';
import type { Draft, DraftRevision, DraftRevisionPage, Problem } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/Table';
import { usePlatform } from '../app/platform';
import type { DraftDiffView } from '../app/router';
import { revisionAuthorView } from '../app/status';
import { toProblem, useAsync } from '../app/useAsync';
import { formatDateTime } from '../lib/datetime';
import { RevisionDiffPanel } from './RevisionDiffPanel';

export interface DraftHistoryProps {
  workspaceId: string;
  appId: string;
  draft: Draft;
  /** 差分を見ている版（URL の rev）。 */
  revisionId: string | null;
  diffView: DraftDiffView;
  onSelectRevision(revisionId: string | null): void;
  onSelectDiffView(view: DraftDiffView): void;
  /** restore の応答で受け取った新しい Draft。 */
  onDraftChange(draft: Draft): void;
}

const short = (id: string) => id.slice(0, 8);

/**
 * Draft の版の履歴（新しい順）。差分と restore の入口。
 * head が進んだら（保存・restore・SSE の取り直し）最初のページから取り直す。
 */
export function DraftHistory({
  workspaceId,
  appId,
  draft,
  revisionId,
  diffView,
  onSelectRevision,
  onSelectDiffView,
  onDraftChange,
}: DraftHistoryProps) {
  const { api } = usePlatform();
  const first = useAsync(
    () => api.listDraftRevisions(workspaceId, appId),
    [api, workspaceId, appId, draft.headRevisionId],
  );
  const [pages, setPages] = useState<{
    first: DraftRevisionPage | null;
    rest: DraftRevision[];
    nextCursor: string | null;
  }>({ first: null, rest: [], nextCursor: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreProblem, setMoreProblem] = useState<Problem | null>(null);
  const [restoring, setRestoring] = useState<DraftRevision | null>(null);

  // 最初のページを取り直したら、取り足した分は捨てる。effect で合わせると古い行が 1 度描かれるので描画中に比べる。
  if (first.data !== pages.first) {
    setPages({ first: first.data, rest: [], nextCursor: first.data?.nextCursor ?? null });
  }

  const loadMore = async () => {
    const cursor = pages.nextCursor;
    const base = pages.first;
    if (!cursor) return;
    setLoadingMore(true);
    setMoreProblem(null);
    try {
      const next = await api.listDraftRevisions(workspaceId, appId, { cursor });
      // 取り足しの間に最初のページが取り直されていたら、古い続きは足さない。
      setPages((current) =>
        current.first === base
          ? { first: base, rest: [...current.rest, ...next.items], nextCursor: next.nextCursor }
          : current,
      );
    } catch (error) {
      setMoreProblem(toProblem(error));
    } finally {
      setLoadingMore(false);
    }
  };

  const items = pages.first ? [...pages.first.items, ...pages.rest] : [];
  const selected = revisionId ? (items.find((item) => item.id === revisionId) ?? null) : null;

  return (
    <div className="stack">
      {first.loading && !first.data && <Spinner label="版の履歴を取得しています" />}
      <ProblemNotice problem={first.problem} action={<Button onClick={first.reload}>再取得</Button>} />
      {first.data && items.length === 0 && <EmptyState title="版がまだありません" />}
      {items.length > 0 && (
        <Table
          label="版の履歴"
          columns={[
            { key: 'id', header: '版' },
            { key: 'author', header: '作者' },
            { key: 'summary', header: '内容' },
            { key: 'fileCount', header: 'ファイル数' },
            { key: 'createdAt', header: '日時' },
            { key: 'actions', header: '操作' },
          ]}
          rows={items.map((revision) => {
            const author = revisionAuthorView(revision.author);
            const isHead = revision.id === draft.headRevisionId;
            return {
              key: revision.id,
              cells: {
                id: (
                  <span className="status-cell">
                    <code>{short(revision.id)}</code>
                    {isHead && <Badge tone="completed">現在の版</Badge>}
                    {/* 公開した版は Draft を捨てても記録が残る（G1・G22）。 */}
                    {revision.publishedVersion !== null && (
                      <Badge tone="completed">{`公開 v${revision.publishedVersion}`}</Badge>
                    )}
                  </span>
                ),
                author: <Badge tone={author.tone}>{author.label}</Badge>,
                summary: revision.summary || '—',
                fileCount: revision.fileCount,
                createdAt: formatDateTime(revision.createdAt),
                actions: (
                  <span className="row">
                    <Button
                      variant="text"
                      aria-current={revision.id === revisionId ? 'true' : undefined}
                      onClick={() => onSelectRevision(revision.id)}
                    >
                      差分を見る
                    </Button>
                    {/* 現在の版へ戻しても版は増えない（API は 200）ので押させない。 */}
                    <Button variant="text" disabled={isHead} onClick={() => setRestoring(revision)}>
                      この版に戻す
                    </Button>
                  </span>
                ),
              },
            };
          })}
        />
      )}
      {pages.nextCursor && (
        <div className="row">
          <Button disabled={loadingMore} onClick={() => void loadMore()}>
            さらに読み込む
          </Button>
        </div>
      )}
      <ProblemNotice problem={moreProblem} />
      {revisionId && selected && (
        <Card title={`版 ${short(selected.id)} の差分`}>
          <RevisionDiffPanel
            workspaceId={workspaceId}
            appId={appId}
            revision={selected}
            view={diffView}
            onSelectView={onSelectDiffView}
          />
        </Card>
      )}
      {revisionId && !selected && first.data && (
        <p className="hint">{`版 ${short(revisionId)} は読み込んだ履歴にありません。「さらに読み込む」で探してください。`}</p>
      )}
      {restoring && (
        <ConfirmDialog
          title={`版 ${short(restoring.id)} に戻す`}
          body={<p>この版のファイル表で新しい版を作ります。今の版は履歴に残り、あとから戻せます。</p>}
          confirmLabel="この版に戻す"
          onConfirm={async (idempotencyKey) => {
            const mutation = await api.restoreDraftRevision(workspaceId, appId, restoring.id, idempotencyKey);
            setRestoring(null);
            onDraftChange(mutation.draft);
          }}
          onClose={() => setRestoring(null)}
        />
      )}
    </div>
  );
}
